"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Body,
  Button,
  Checkbox,
  CloseIcon,
  Heading,
  Input,
  Label,
  PhoneNumberInput,
  RadioGroup,
  RadioGroupItem,
} from "@scott/ui-d2c";
import { Loader2 } from "lucide-react";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Controller, type FieldError, useForm } from "react-hook-form";
import {
  verifyRecaptchaV2,
  verifyRecaptchaV3,
} from "@/actions/recaptcha-action";
import { newsletterAction } from "@/app/[country]/[lang]/company/newsletter/actions/newsletter-action";
import { ThankYou } from "@/components/newsletter/thank-you";
import { RichTextDisplay } from "@/components/rich-text-display";
import { useServerError } from "@/hooks/use-server-error";
import { useConfig } from "@/providers/config-provider";
import { getNewsletterSchema } from "@/schemas/newsletter.schema";
import type { NewsletterForm, NewsletterTypes } from "@/types/newsletter";
import { handleFieldErrors } from "@/utils/common.utils";
import {
  errorCodesToCheck,
  languages,
  RichTextLinkType,
  TranslationKeys,
} from "@/utils/constants";
import { debugInfo } from "@/utils/debug";
import { pushEvent } from "@/utils/gtm.utils";
import {
  computedDatas,
  InterestsByBrand,
  USInterests,
} from "@/utils/newsletter.utils";
import {
  getRecaptchaV3Token,
  RECAPTCHA_V3_ACTIONS,
} from "@/utils/recaptcha-v3-client";

// ReCAPTCHA configuration
const recaptchaV2SiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY;
const RECAPTCHA_TIMEOUT_MS = 8000;
const RECAPTCHA_V3_MIN_SCORE = 0.5;

interface NewsletterProps {
  userEmail: { encrypted: string; decrypted: string };
  data?: NewsletterTypes;
}

export const Newsletter = ({ userEmail, data }: NewsletterProps) => {
  const { brand } = useConfig();

  const t = useTranslations("newsletterPage");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const newsletterSchema = getNewsletterSchema(t, brand.value);
  const { serverError, setServerError, handleCleanError } = useServerError();
  const [isPending, startTransition] = useTransition();

  const recaptchaV2Ref = useRef<ReCAPTCHA>(null);

  const [showV2Challenge, setShowV2Challenge] = useState(false);

  const params = useParams();
  const countryParam = (params.country as string) || "global";
  const languageParam = (params.lang as string) || "en";

  const isLanguageSelectionVisible = countryParam === "global";
  const isAllowedForCurrentCountry = countryParam === "us";
  const submitButtonContent = isPending ? (
    <Loader2 className="animate-spin" />
  ) : (
    t("form.submitButton.label")
  );

  const privacyPolicyDefaultValue = countryParam === "us";
  const languagesForBrand = languages[brand.value] || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    setError,
  } = useForm<NewsletterForm>({
    resolver: zodResolver<NewsletterForm>(newsletterSchema),
    defaultValues: {
      email: userEmail.decrypted || data?.email || "",
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      phoneNumber: data?.phoneNumber || "",
      interest: data?.interest || [],
      hasPrivacyPolicy: data?.hasPrivacyPolicy || privacyPolicyDefaultValue,
      preferredLanguage: isLanguageSelectionVisible ? "" : languageParam,
      ietf_language_tag: "en",
      country: countryParam,
    },
  });

  const hasPrivacyPolicy =
    countryParam === "us" ? true : watch("hasPrivacyPolicy");

  const pushNewsletterEvent = useCallback(
    (interest: string[] = []) => {
      pushEvent("generate_lead", {
        form: {
          type: "Newsletter",
          topic: "",
          division: brand.value === "scott" ? interest.join(",") : "",
        },
      });
    },
    [brand.value]
  );

  const processNewsletterSubmission = useCallback(
    async (formData: NewsletterForm) => {
      const finalData = computedDatas(
        formData,
        languageParam,
        countryParam,
        isLanguageSelectionVisible,
        languagesForBrand.map((l) => l.value),
        brand.value
      );

      const response = await newsletterAction(finalData);

      if (response && "error" in response) {
        if (response.error.fieldErrors) {
          handleFieldErrors<NewsletterForm>(
            response.error.fieldErrors,
            setError as unknown as (
              field: keyof NewsletterForm,
              error: { type: string; message: string }
            ) => void
          );
          return false;
        }
        if (response.error.generalError) {
          setServerError(response.error.generalError);
          return false;
        }
      }

      setIsSubmitted(true);
      pushNewsletterEvent(formData.interest as string[]);
      return true;
    },
    [
      brand,
      languageParam,
      countryParam,
      isLanguageSelectionVisible,
      languagesForBrand,
      setError,
      setServerError,
      pushNewsletterEvent,
    ]
  );

  const onSubmit = (formData: NewsletterForm) => {
    handleCleanError();
    setShowV2Challenge(false);

    startTransition(async () => {
      try {
        const v3SiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY;
        const token = v3SiteKey
          ? await Promise.race([
              getRecaptchaV3Token(v3SiteKey, RECAPTCHA_V3_ACTIONS.NEWSLETTER, {
                hl: languageParam || undefined,
              }),
              new Promise<string | null>((resolve) => {
                setTimeout(() => {
                  resolve(null);
                }, RECAPTCHA_TIMEOUT_MS);
              }),
            ])
          : null;

        if (!token) {
          debugInfo(
            "Failed to generate reCAPTCHA V3 token, proceeding without validation."
          );
          await processNewsletterSubmission(formData);
          return;
        }

        const v3Result = await verifyRecaptchaV3(
          token,
          RECAPTCHA_V3_ACTIONS.NEWSLETTER
        );

        debugInfo("reCAPTCHA v3 verification result:", v3Result);

        if (
          !v3Result.success ||
          (v3Result.score && v3Result.score <= RECAPTCHA_V3_MIN_SCORE)
        ) {
          if (
            v3Result.errorCodes?.some((code) =>
              errorCodesToCheck.includes(code)
            )
          ) {
            debugInfo(
              "reCAPTCHA v3 verification had connection issues, proceeding without validation."
            );
            await processNewsletterSubmission(formData);
            return;
          }
          if (recaptchaV2SiteKey) {
            setShowV2Challenge(true);
          } else {
            await processNewsletterSubmission(formData);
          }
          return;
        }

        await processNewsletterSubmission(formData);
      } catch (_error) {
        setServerError(TranslationKeys.DataError);
      }
    });
  };

  const handleV2Submission = async () => {
    const v2Token = recaptchaV2Ref.current?.getValue() || "";
    const formData = watch();

    if (!v2Token) {
      setServerError(
        "Failed to get token verify site key, proceeding without validation."
      );
      await processNewsletterSubmission(formData);
    }

    try {
      const v2Result = await verifyRecaptchaV2(v2Token);

      if (v2Result.success) {
        await processNewsletterSubmission(formData);
        setShowV2Challenge(false);
      } else {
        if (
          v2Result.errorCodes?.some((code) => errorCodesToCheck.includes(code))
        ) {
          await processNewsletterSubmission(formData);
          setShowV2Challenge(false);
          return;
        }
        setServerError("Verification challenge failed. Please try again.");
      }
    } catch (_error) {
      setServerError(TranslationKeys.DataError);
    }
  };

  useEffect(() => {
    if (userEmail?.decrypted) {
      setValue("email", userEmail.decrypted);
    }
  }, [userEmail, setValue]);

  return (
    <div>
      {isSubmitted ? (
        <ThankYou isDoubleOptin={false} translation="newsletterPage" />
      ) : (
        <div className="mx-auto max-w-xl py-16">
          <Heading variant="h1" wrapperClass="items-start">
            {countryParam === "us" ? t("usTitle") : t("title")}
          </Heading>
          <p className="mt-16 pb-4">
            {countryParam === "us" ? t("usDescription") : t("description")}
          </p>
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-6">
              <div>
                <Input
                  id="email"
                  label={`${t("form.emailInput.label")}*`}
                  {...register("email")}
                  hasIcon={false}
                  message={(errors.email as FieldError)?.message || ""}
                  placeholder={t("form.emailInput.label")}
                  status={errors.email ? "error" : null}
                  type="email"
                />
              </div>
              {isAllowedForCurrentCountry && (
                <div>
                  <Input
                    id="firstName"
                    label={t("form.firstName.label")}
                    {...register("firstName")}
                    hasIcon={false}
                    message={(errors.firstName as FieldError)?.message || ""}
                    placeholder={t("form.firstName.label")}
                    status={errors.firstName ? "error" : null}
                    type="text"
                  />
                </div>
              )}
              {isAllowedForCurrentCountry && (
                <div>
                  <Input
                    id="lastName"
                    label={t("form.lastName.label")}
                    {...register("lastName")}
                    hasIcon={false}
                    message={(errors.lastName as FieldError)?.message || ""}
                    placeholder={t("form.lastName.label")}
                    status={errors.lastName ? "error" : null}
                    type="text"
                  />
                </div>
              )}
              {isAllowedForCurrentCountry && (
                <div>
                  <PhoneNumberInput
                    label={t("form.phoneNumber.label")}
                    name="phoneNumber"
                    onChange={(phone) => setValue("phoneNumber", phone)}
                  />
                </div>
              )}
              {isLanguageSelectionVisible && (
                <div className="space-y-4">
                  <Label className="font-bold">
                    {t("form.preferredLangRadio.label")}*
                  </Label>
                  <Controller
                    control={control}
                    name="preferredLanguage"
                    render={({ field }) => (
                      <RadioGroup
                        message={
                          (errors.preferredLanguage as FieldError)?.message ||
                          ""
                        }
                        onValueChange={(value) => field.onChange(value)}
                        status={errors.preferredLanguage ? "error" : undefined}
                        value={field.value}
                      >
                        {languagesForBrand.map((l) => (
                          <div
                            className="flex items-center space-x-2"
                            key={l.value}
                          >
                            <RadioGroupItem
                              aria-label={t(
                                `form.preferredLangRadio.${l.label.toLowerCase()}`
                              )}
                              description={""}
                              label={t(
                                `form.preferredLangRadio.${l.label.toLowerCase()}`
                              )}
                              value={l.value}
                              variant={
                                errors.preferredLanguage ? "invalid" : "neutral"
                              }
                            />
                          </div>
                        ))}
                      </RadioGroup>
                    )}
                  />
                </div>
              )}
              {brand.isScott && (
                <div className="space-y-2">
                  <Label className="font-bold">
                    {t("form.interestsCheck.label")}*
                  </Label>
                  <p className="pb-2">{t("form.interestsCheck.description")}</p>
                  <Controller
                    control={control}
                    name="interest"
                    render={({ field }) => (
                      <div className="grid grid-cols-2 gap-2">
                        {(countryParam === "us"
                          ? USInterests
                          : InterestsByBrand[brand.value]
                        ).map((i) => (
                          <div key={i}>
                            <Checkbox
                              aria-label={t(`form.interestsCheck.${i}`)}
                              checked={field.value?.includes(i)}
                              className={
                                errors.interest ? "!border-danger-medium" : ""
                              }
                              id={i}
                              label={t(`form.interestsCheck.${i}`)}
                              labelClassName={
                                errors.interest ? "!text-error" : ""
                              }
                              onCheckedChange={(checked) => {
                                const newValue = checked
                                  ? [...(field.value || []), i]
                                  : field.value?.filter(
                                      (item: string) => item !== i
                                    );
                                field.onChange(newValue);
                              }}
                              variant={"sm"}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  />
                  {errors.interest && (
                    <span className="flex items-center gap-1">
                      <CloseIcon
                        className="h-3 w-3 rounded-full text-error"
                        size="sm"
                      />
                      <Body className="text-error" size="xs">
                        {(errors.interest as FieldError)?.message || ""}
                      </Body>
                    </span>
                  )}
                </div>
              )}
              {isAllowedForCurrentCountry && (
                <div>
                  <span>
                    {t("form.usPrivacyPolicy.message")}{" "}
                    <a
                      className="underline hover:text-gray-600"
                      href={`/${countryParam}/${languageParam}/company/policy`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {t("form.usPrivacyPolicy.link1Label")}
                    </a>
                    <span className="mx-1">&amp;</span>
                    <a
                      className="underline hover:text-gray-600"
                      href="https://scott-usa.myklpages.com/p/mobile-terms-of-service"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {t("form.usPrivacyPolicy.link2Label")}
                    </a>
                  </span>
                </div>
              )}
              <div>
                {countryParam !== "us" && (
                  <Controller
                    control={control}
                    name="hasPrivacyPolicy"
                    render={({ field }) => (
                      <Checkbox
                        aria-label="Privacy-Policy"
                        checked={field.value}
                        id="hasPrivacyPolicy"
                        label={
                          <span>
                            {t.rich(
                              "form.privacyPolicyCheck.privacyPolicyLabel",
                              {
                                link: (chunks) => (
                                  <a
                                    className="text-brand-medium underline"
                                    href={`/${countryParam}/${languageParam}/company/policy`}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                  >
                                    {chunks}
                                  </a>
                                ),
                              }
                            )}{" "}
                            <span className="text-danger-medium">*</span>
                          </span>
                        }
                        message={
                          (errors.hasPrivacyPolicy as FieldError)?.message || ""
                        }
                        onCheckedChange={field.onChange}
                        status={errors.hasPrivacyPolicy ? "error" : undefined}
                        variant="sm"
                      />
                    )}
                  />
                )}
                {showV2Challenge && recaptchaV2SiteKey && (
                  <div className="mt-6">
                    <ReCAPTCHA
                      onChange={handleV2Submission}
                      ref={recaptchaV2Ref}
                      sitekey={recaptchaV2SiteKey}
                    />
                  </div>
                )}
              </div>
              {serverError && (
                <p className="mt-1 text-error text-sm" role="alert">
                  <RichTextDisplay
                    linkType={RichTextLinkType.CONTACT_US}
                    translationKey={serverError}
                    translationName={"newsletterPage"}
                  />
                </p>
              )}
              <div className="pb-2">
                <Button
                  disabled={isPending || !hasPrivacyPolicy || showV2Challenge}
                  size="md"
                  type="submit"
                  variant="solid"
                >
                  {submitButtonContent}
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
